import axios, { type AxiosRequestConfig } from "axios";
import { createNewAxiosInstance } from "@/api/axios-instance";
import { ASSISTANT_PROXY_PATH } from "./assistant-endpoint";
import type { ConversationSummary, QuestionImage, QuestionView, Vote } from "./assistant-types";
import { type AnswerPolling, waitForAnswer } from "./wait-for-answer";

const LOCAL_CALLER_HEADER = "X-Local-Caller-Username";
const ANSWER_POLL_INTERVAL_MS = 2_000;
/** Covers the bot's 90 s Question timeout plus its wrap-up turn. */
const ANSWER_DEADLINE_MS = 150_000;
const SERVICE_DOWN_MESSAGE =
  "Can't reach the Support Bot. Check it is running on the dev server, or locally if VITE_ASSISTANT_LOCAL_URL is set.";

/**
 * The Echo API client, so every call carries the Bearer access token and x-session-id the bot verifies with MS32,
 * refreshed the same way as for the Echo services. The bot's JSON is not in the Echo response envelope, so each call
 * skips the envelope transform.
 */
const assistantClient = createNewAxiosInstance();

export class AssistantApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "AssistantApiError";
  }
}

interface ErrorBody {
  error?: string;
  issues?: Array<{ message?: string }>;
}

function assistantErrorOf(error: unknown): AssistantApiError {
  if (!axios.isAxiosError(error) || error.response === undefined)
    return new AssistantApiError(0, SERVICE_DOWN_MESSAGE);
  const { status } = error.response;
  const body = error.response.data as ErrorBody | undefined;
  const message =
    body?.issues?.[0]?.message ??
    body?.error ??
    (status >= 500 ? SERVICE_DOWN_MESSAGE : `HTTP ${status}`);
  return new AssistantApiError(status, message);
}

async function callAssistant<T>(
  path: string,
  callerUsername: string | undefined,
  request: Pick<AxiosRequestConfig, "method" | "data">,
): Promise<T> {
  try {
    const response = await assistantClient.request<T>({
      ...request,
      url: `${ASSISTANT_PROXY_PATH}${path}`,
      headers: callerUsername === undefined ? {} : { [LOCAL_CALLER_HEADER]: callerUsername },
      skipResponseTransform: true,
    } as AxiosRequestConfig & { skipResponseTransform: boolean });
    return response.data;
  } catch (error) {
    throw assistantErrorOf(error);
  }
}

export async function startConversation(callerUsername: string | undefined): Promise<string> {
  const { conversationId } = await callAssistant<{ conversationId: string }>(
    "/conversations",
    callerUsername,
    { method: "POST" },
  );
  return conversationId;
}

/** How many recent Conversations the panel offers to resume. */
export const RECENT_CONVERSATIONS = 5;

/** The Caller's latest Conversations started in the panel, newest first. */
export async function listRecentConversations(
  callerUsername: string | undefined,
): Promise<ConversationSummary[]> {
  const { conversations } = await callAssistant<{ conversations: ConversationSummary[] }>(
    `/conversations?limit=${RECENT_CONVERSATIONS}`,
    callerUsername,
    { method: "GET" },
  );
  return conversations;
}

/** A Conversation's Questions and Answers in asking order, to resume it. */
export async function conversationTurns(
  conversationId: string,
  callerUsername: string | undefined,
): Promise<QuestionView[]> {
  const { turns } = await callAssistant<{ turns: QuestionView[] }>(
    `/conversations/${conversationId}`,
    callerUsername,
    { method: "GET" },
  );
  return turns;
}

/**
 * Asks, then polls the Question until it settles. Waiting on the ask itself (the API's ?wait=true) would hold one
 * request open for the whole Answer, which the API Gateway cuts off at 29 s.
 */
export async function askQuestion(
  conversationId: string,
  callerUsername: string | undefined,
  question: { text: string; images: QuestionImage[] },
): Promise<QuestionView> {
  const asked = await callAssistant<QuestionView>(
    `/conversations/${conversationId}/questions`,
    callerUsername,
    { method: "POST", data: question },
  );
  return waitForAnswer(asked, answerPolling(callerUsername), {
    intervalMs: ANSWER_POLL_INTERVAL_MS,
    deadlineMs: ANSWER_DEADLINE_MS,
  });
}

function answerPolling(callerUsername: string | undefined): AnswerPolling {
  return {
    fetchQuestion: (questionId) =>
      callAssistant<QuestionView>(`/questions/${questionId}`, callerUsername, { method: "GET" }),
    pause: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
    now: () => Date.now(),
  };
}

export function sendFeedback(
  questionId: string,
  callerUsername: string | undefined,
  vote: Vote,
): Promise<void> {
  return callAssistant<void>(`/questions/${questionId}/feedback`, callerUsername, {
    method: "POST",
    data: { vote },
  });
}
